import React, { useState, useEffect } from 'react';

const AccessCardLoader = ({ onComplete, duration = 2000, authResult = null, isMorphing = false, isFinalizingAuth = false }) => {
  const [stage, setStage] = useState('loading');
  const [statusText, setStatusText] = useState('Initializing Card Reader...');

  useEffect(() => {
    let statusSequence;
    
    if (isFinalizingAuth) {
      // Different sequence for finalizing authentication
      statusSequence = [
        { time: duration * 0.15, text: 'Finalizing authentication...' },
        { time: duration * 0.35, text: 'Verifying your credentials...' },
        { time: duration * 0.55, text: 'Setting up your secure session...' },
        { time: duration * 0.75, text: 'Preparing your dashboard...' },
        { time: duration * 0.90, text: 'Almost ready...' },
        { time: duration, text: 'AUTHENTICATION COMPLETE!' }
      ];
    } else {
      // Original sequence for initial authentication
      statusSequence = [
        { time: duration * 0.15, text: 'Detecting access credentials...' },
        { time: duration * 0.30, text: 'Reading authentication token...' },
        { time: duration * 0.50, text: 'Validating user session...' },
        { time: duration * 0.70, text: 'Checking access permissions...' },
        { time: duration * 0.85, text: 'Authenticating credentials...' },
        { time: duration, text: authResult === false ? 'ACCESS DENIED!' : 'ACCESS GRANTED!' }
      ];
    }

    const timers = statusSequence.map(({ time, text }) =>
      setTimeout(() => setStatusText(text), time)
    );

    // Success/Failure state based on authResult
    const statusTimer = setTimeout(() => {
      if (isFinalizingAuth) {
        setStage('success'); // Always success for finalizing
      } else {
        setStage(authResult === false ? 'failure' : 'success');
      }
    }, duration);

    // Complete and fade out after showing final state
    const completeTimer = setTimeout(() => {
      setStage('complete');
      if (onComplete) onComplete();
    }, duration + 1000);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(statusTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, duration, authResult, isFinalizingAuth]);

  const styles = {
    loadingContainer: {
      background: isMorphing 
        ? 'linear-gradient(135deg, rgba(248, 250, 252, 0.5), rgba(241, 245, 249, 0.6))'
        : isFinalizingAuth
        ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.05), rgba(16, 185, 129, 0.08))'
        : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.99))',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: isFinalizingAuth ? '350px' : '400px',
      width: '500px',
      maxWidth: '90vw',
      borderRadius: isMorphing ? '50px' : '20px',
      boxShadow: isMorphing 
        ? '0 10px 25px rgba(0, 0, 0, 0.05)' 
        : isFinalizingAuth
        ? '0 20px 40px rgba(34, 197, 94, 0.1)'
        : '0 25px 50px rgba(0, 0, 0, 0.15)',
      border: isMorphing 
        ? '1px solid rgba(148, 163, 184, 0.1)' 
        : isFinalizingAuth
        ? '1px solid rgba(34, 197, 94, 0.2)'
        : '1px solid rgba(148, 163, 184, 0.3)',
      opacity: stage === 'complete' ? 0 : 1,
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      backdropFilter: isMorphing ? 'blur(5px)' : 'blur(20px)',
      position: 'relative',
      transform: isMorphing ? 'scale(0.7)' : 'scale(1)'
    },
    accessContainer: {
      position: 'relative',
      width: '180px',
      height: '100px',
      margin: '20px 0',
      opacity: isMorphing ? 0 : 1,
      transform: isMorphing ? 'scale(0.5) rotate(180deg)' : 'scale(1) rotate(0deg)',
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    },
    accessCard: {
      width: '120px',
      height: '75px',
      background: stage === 'success' 
        ? 'linear-gradient(135deg, #22c55e, #16a34a)'
        : stage === 'failure'
        ? 'linear-gradient(135deg, #ef4444, #dc2626)'
        : 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
      borderRadius: '10px',
      position: 'relative',
      border: stage === 'success' 
        ? '2px solid #16a34a' 
        : stage === 'failure'
        ? '2px solid #dc2626'
        : '2px solid #94a3b8',
      animation: stage !== 'complete' ? 'cardInsert 3s ease-in-out infinite' : 'none',
      boxShadow: stage === 'success'
        ? '0 10px 30px rgba(34, 197, 94, 0.4)'
        : stage === 'failure'
        ? '0 10px 30px rgba(239, 68, 68, 0.4)'
        : '0 10px 25px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease-in-out',
    },
    cardChip: {
      position: 'absolute',
      width: '22px',
      height: '16px',
      background: stage === 'success' 
        ? '#fbbf24' 
        : stage === 'failure'
        ? '#64748b'
        : '#f59e0b',
      borderRadius: '3px',
      bottom: '12px',
      left: '10px',
      boxShadow: stage === 'success' 
        ? '0 0 10px rgba(251, 191, 36, 0.6)'
        : stage === 'failure'
        ? '0 2px 4px rgba(0, 0, 0, 0.3)'
        : '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease-in-out',
    },
    cardStripes: {
      position: 'absolute',
      width: '35px',
      height: '3px',
      background: stage === 'success' 
        ? '#f8fafc' 
        : stage === 'failure'
        ? '#fca5a5'
        : '#64748b',
      top: '15px',
      right: '10px',
      borderRadius: '1px',
      transition: 'background 0.3s ease-in-out',
    },
    cardStripe2: {
      position: 'absolute',
      width: '30px',
      height: '3px',
      background: stage === 'success' 
        ? '#f8fafc' 
        : stage === 'failure'
        ? '#fca5a5'
        : '#64748b',
      top: '22px',
      right: '10px',
      borderRadius: '1px',
      transition: 'background 0.3s ease-in-out',
    },
    cardStripe3: {
      position: 'absolute',
      width: '38px',
      height: '3px',
      background: stage === 'success' 
        ? '#f8fafc' 
        : stage === 'failure'
        ? '#fca5a5'
        : '#64748b',
      top: '29px',
      right: '10px',
      borderRadius: '1px',
      transition: 'background 0.3s ease-in-out',
    },
    cardMagStripe: {
      position: 'absolute',
      width: '95px',
      height: '10px',
      background: stage === 'success' 
        ? 'linear-gradient(90deg, #374151, #1f2937)'
        : stage === 'failure'
        ? 'linear-gradient(90deg, #7f1d1d, #991b1b)'
        : 'linear-gradient(90deg, #475569, #334155)',
      bottom: '25px',
      left: '12px',
      borderRadius: '2px',
      animation: stage !== 'complete' ? 'magneticRead 2s ease-in-out infinite' : 'none',
      transition: 'background 0.3s ease-in-out',
    },
    cardReader: {
      position: 'absolute',
      width: '150px',
      height: '15px',
      background: stage === 'success' 
        ? 'linear-gradient(135deg, #22c55e, #16a34a)'
        : stage === 'failure'
        ? 'linear-gradient(135deg, #ef4444, #dc2626)'
        : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
      bottom: '0',
      left: '15px',
      border: stage === 'success' 
        ? '1px solid #16a34a' 
        : stage === 'failure'
        ? '1px solid #dc2626'
        : '1px solid #cbd5e1',
      borderRadius: '4px',
      boxShadow: stage === 'success'
        ? '0 0 20px rgba(34, 197, 94, 0.4)'
        : stage === 'failure'
        ? '0 0 20px rgba(239, 68, 68, 0.4)'
        : '0 3px 6px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease-in-out',
    },
    readerSlot: {
      position: 'absolute',
      width: '120px',
      height: '5px',
      background: 'rgba(0, 0, 0, 0.3)',
      top: '5px',
      left: '15px',
      borderRadius: '2px',
    },
    readerLight: {
      position: 'absolute',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      right: '8px',
      top: '2px',
      animation: stage !== 'complete' ? 'readerStatus 3s ease-in-out infinite' : 'none',
      background: stage === 'success' 
        ? '#22c55e' 
        : stage === 'failure'
        ? '#ef4444'
        : '#ef4444',
      boxShadow: stage === 'success' 
        ? '0 0 15px #22c55e'
        : stage === 'failure'
        ? '0 0 15px #ef4444'
        : '0 0 10px #ef4444',
      transition: 'all 0.3s ease-in-out',
    },
    statusText: {
      marginTop: '30px',
      fontSize: isFinalizingAuth ? '16px' : '18px',
      color: stage === 'success' 
        ? '#22c55e' 
        : stage === 'failure'
        ? '#ef4444'
        : isFinalizingAuth
        ? '#16a34a'
        : '#475569',
      fontWeight: '600',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      textShadow: stage === 'success' 
        ? '0 0 10px #22c55e' 
        : stage === 'failure'
        ? '0 0 10px #ef4444'
        : isFinalizingAuth
        ? '0 0 8px rgba(34, 197, 94, 0.3)'
        : 'none',
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      textAlign: 'center',
      maxWidth: '450px',
      padding: '0 20px',
      opacity: isMorphing ? 0 : 1,
      transform: isMorphing ? 'translateY(20px)' : 'translateY(0px)',
      animation: isFinalizingAuth && !isMorphing ? 'finalizingTextPulse 2s ease-in-out infinite' : 'none'
    },
    securityBadge: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      background: stage === 'success' 
        ? 'rgba(34, 197, 94, 0.15)'
        : stage === 'failure'
        ? 'rgba(239, 68, 68, 0.15)'
        : isFinalizingAuth
        ? 'rgba(34, 197, 94, 0.12)'
        : 'rgba(59, 130, 246, 0.1)',
      color: stage === 'success' 
        ? '#22c55e' 
        : stage === 'failure'
        ? '#ef4444'
        : isFinalizingAuth
        ? '#16a34a'
        : '#3b82f6',
      padding: '8px 16px',
      borderRadius: '12px',
      fontSize: '13px',
      fontWeight: '600',
      border: stage === 'success'
        ? '1px solid rgba(34, 197, 94, 0.4)'
        : stage === 'failure'
        ? '1px solid rgba(239, 68, 68, 0.4)'
        : isFinalizingAuth
        ? '1px solid rgba(34, 197, 94, 0.3)'
        : '1px solid rgba(59, 130, 246, 0.3)',
      boxShadow: stage === 'success'
        ? '0 0 15px rgba(34, 197, 94, 0.3)'
        : stage === 'failure'
        ? '0 0 15px rgba(239, 68, 68, 0.3)'
        : isFinalizingAuth
        ? '0 0 12px rgba(34, 197, 94, 0.2)'
        : '0 0 10px rgba(59, 130, 246, 0.2)',
      transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      opacity: isMorphing ? 0 : 1,
      transform: isMorphing ? 'translateX(-50%) scale(0.5)' : 'translateX(-50%) scale(1)',
      animation: isFinalizingAuth && !isMorphing ? 'finalizingBadgePulse 3s ease-in-out infinite' : 'none'
    },
  };

  const keyframes = `
    @keyframes cardInsert {
      0% { transform: translateX(-40px) rotateY(10deg); opacity: 0.7; }
      30% { transform: translateX(0px) rotateY(0deg); opacity: 1; }
      70% { transform: translateX(0px) rotateY(0deg); opacity: 1; }
      100% { transform: translateX(-40px) rotateY(10deg); opacity: 0.7; }
    }
    
    @keyframes magneticRead {
      0%, 100% { 
        box-shadow: inset 0 0 0 rgba(59, 130, 246, 0); 
      }
      50% { 
        box-shadow: inset 0 0 15px rgba(59, 130, 246, 0.7); 
      }
    }
    
    @keyframes readerStatus {
      0%, 25% { 
        background: #ef4444; 
        box-shadow: 0 0 10px #ef4444;
      }
      30%, 70% { 
        background: #22c55e; 
        box-shadow: 0 0 20px #22c55e;
      }
      75%, 100% { 
        background: #ef4444; 
        box-shadow: 0 0 10px #ef4444;
      }
    }
    
    @keyframes finalizingTextPulse {
      0%, 100% { 
        opacity: 1; 
        transform: translateY(0px) scale(1); 
      }
      50% { 
        opacity: 0.8; 
        transform: translateY(-2px) scale(1.02); 
      }
    }
    
    @keyframes finalizingBadgePulse {
      0%, 100% { 
        box-shadow: 0 0 12px rgba(34, 197, 94, 0.2);
        transform: translateX(-50%) scale(1);
      }
      50% { 
        box-shadow: 0 0 20px rgba(34, 197, 94, 0.4);
        transform: translateX(-50%) scale(1.05);
      }
    }
  `;

  const getBadgeText = () => {
    if (stage === 'success') return 'AUTHENTICATION COMPLETE';
    if (stage === 'failure') return 'ACCESS DENIED';
    if (isFinalizingAuth) return 'FINALIZING SESSION';
    return 'CARD AUTHENTICATION';
  };

  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.loadingContainer}>
        <div style={styles.securityBadge}>
          {getBadgeText()}
        </div>
        
        <div style={styles.accessContainer}>
          <div style={styles.accessCard}>
            <div style={styles.cardChip}></div>
            <div style={styles.cardStripes}></div>
            <div style={styles.cardStripe2}></div>
            <div style={styles.cardStripe3}></div>
            <div style={styles.cardMagStripe}></div>
          </div>
          
          <div style={styles.cardReader}>
            <div style={styles.readerSlot}></div>
            <div style={styles.readerLight}></div>
          </div>
        </div>
        
        <div style={styles.statusText}>
          {statusText}
        </div>
      </div>
    </>
  );
};

export default AccessCardLoader;